import { useReducer } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { useAuth } from '@/hooks/useAuth'
import { useGatewayPaymentAlert } from '@/hooks/useGatewayPaymentAlert'
import { useInstitution } from '@/contexts/InstitutionContext'
import { isSidebarNavActive } from '@/lib/sidebar-nav-active'
import {
  canShowInstitutionManagerialDashboardMenu,
  isAdminAdministrationSidebar,
  resolveSidebarMenuItems,
  type SidebarMenuItem,
} from '@/lib/sidebar-menu'
import { useMobileSidebar } from '@/hooks/useMobileSidebar'
import { SidebarItem } from './SidebarItem'
import { SidebarNavGroup } from './SidebarNavGroup'
import styles from './Sidebar.module.css'

function getActiveGroupLabel(
  items: SidebarMenuItem[],
  pathname: string
): string | null {
  for (const item of items) {
    if (item.kind !== 'group') continue
    const hasActiveChild = item.children.some((child) =>
      isSidebarNavActive(pathname, child)
    )
    if (hasActiveChild) return item.label
  }
  return null
}

type SidebarExpandState = {
  expanded: string | null
  pathname: string
  syncedActive: string | null
}

type SidebarExpandAction =
  | {
      type: 'sync_route'
      pathname: string
      activeGroupLabel: string | null
    }
  | { type: 'toggle'; groupLabel: string }

function sidebarExpandReducer(
  state: SidebarExpandState,
  action: SidebarExpandAction
): SidebarExpandState {
  switch (action.type) {
    case 'sync_route': {
      const { pathname, activeGroupLabel } = action
      if (
        pathname === state.pathname &&
        activeGroupLabel === state.syncedActive
      ) {
        return state
      }
      return {
        pathname,
        syncedActive: activeGroupLabel,
        expanded: activeGroupLabel,
      }
    }
    case 'toggle':
      return {
        ...state,
        expanded:
          state.expanded === action.groupLabel ? null : action.groupLabel,
      }
    default:
      return state
  }
}

const GATEWAY_MENU_PATH = '/gateway-pagamento'

export function Sidebar() {
  const { guard, isCollaborator, managerialDashboardAccess, signOut } = useAuth()
  const { institutionId } = useInstitution()
  const location = useLocation()
  const navigate = useNavigate()
  const showGatewayAlert = useGatewayPaymentAlert()
  const adminAdministrationSidebar = isAdminAdministrationSidebar(guard, institutionId)

  const allItems = resolveSidebarMenuItems(guard, institutionId, {
    canViewManagerialDashboard: canShowInstitutionManagerialDashboardMenu(
      guard,
      isCollaborator,
      managerialDashboardAccess !== null
    ),
  })
  const activeGroupLabel = getActiveGroupLabel(allItems, location.pathname)
  const [expandState, dispatchExpand] = useReducer(sidebarExpandReducer, {
    expanded: activeGroupLabel,
    pathname: location.pathname,
    syncedActive: activeGroupLabel,
  })

  if (
    location.pathname !== expandState.pathname ||
    activeGroupLabel !== expandState.syncedActive
  ) {
    dispatchExpand({
      type: 'sync_route',
      pathname: location.pathname,
      activeGroupLabel,
    })
  }

  const expandedGroupLabel = expandState.expanded

  const handleGroupToggle = (groupLabel: string) => {
    dispatchExpand({ type: 'toggle', groupLabel })
  }

  const { isOpen, isMobileViewport, close } = useMobileSidebar()

  const handleNavLinkClick = () => {
    close()
  }

  const handleSignOut = () => {
    close()
    void signOut().then(() => {
      navigate('/')
    })
  }

  return (
    <aside
      id="app-sidebar"
      className={[styles.sidebar, isOpen && styles.sidebarOpen].filter(Boolean).join(' ')}
      data-open={isOpen ? 'true' : 'false'}
      aria-hidden={isMobileViewport && !isOpen ? true : undefined}
    >
      <Link
        to={adminAdministrationSidebar ? '/home-drftadmin' : '/home'}
        className={styles.logoLink}
        aria-label="Ir para início"
        onClick={handleNavLinkClick}
      >
        <img src="/logo/sortee.png" alt="Sortee" className={styles.logoImg} />
      </Link>
      <nav className={styles.nav} aria-label="Menu principal">
        <ul className={styles.list}>
            {allItems.filter(item => !((isCollaborator || guard === 'donor') && item.kind === 'link' && item.to === '/shop')).map((item) => {
            if (item.kind === 'group') {
              return (
                <li key={item.label}>
                  <SidebarNavGroup
                    label={item.label}
                    icon={item.icon}
                    iconActive={item.iconActive}
                    items={item.children}
                    expanded={expandedGroupLabel === item.label}
                    onToggle={() => handleGroupToggle(item.label)}
                  />
                </li>
              )
            }

            return (
              <li key={item.to + item.label}>
                <SidebarItem
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  exact={item.exact}
                  sectionPrefix={item.sectionPrefix}
                  alert={item.to === GATEWAY_MENU_PATH && showGatewayAlert}
                />
              </li>
            )
          })}
        </ul>
      </nav>
      <div className={styles.footer}>
        <Link to="#" className={styles.footerBtn} onClick={handleNavLinkClick}>
          <span className={styles.footerBtnIcon}>
            <Icon icon="mdi:headset" />
          </span>
          Suporte
        </Link>
        <button type="button" className={styles.footerBtn} onClick={handleSignOut}>
          <span className={styles.footerBtnIcon}>
            <Icon icon="mdi:logout" />
          </span>
          Sair
        </button>
      </div>
    </aside>
  )
}
